import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { HR_CATEGORIES, HR_CATEGORY_ORDER, HR_STATUS, saveBlob } from "@/lib/hr";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Loader2, FileDown, Eye } from "lucide-react";

export default function HRExport() {
  const [cats, setCats] = useState(HR_CATEGORY_ORDER);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [newest, setNewest] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState(false);

  const toggleCat = (c) => setCats((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const runPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/hrdoc/exports/preview", { categories: cats, start, end, newest });
      setPreview(data);
      if (data.count === 0) toast.info("Geen documenten voor deze selectie");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const generate = async () => {
    if (!preview?.plan_id) return;
    setGen(true);
    try {
      const r = await api.post(`/hrdoc/exports/${preview.plan_id}/pdf`, {}, { responseType: "blob" });
      saveBlob(r.data, "HR-documentenoverzicht.pdf");
      toast.success("PDF gegenereerd");
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? "Preview verlopen — maak een nieuwe preview." : formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg);
    } finally { setGen(false); }
  };

  const empName = (id) => preview?.employees?.find((e) => e.id === id)?.name || "Onbekend";

  return (
    <div className="space-y-6" data-testid="hr-export">
      <div>
        <h1 className="text-3xl font-heading font-semibold">Dossier exporteren</h1>
        <p className="text-muted-foreground mt-1">Bundel documenten tot één PDF met inhoudsopgave, geordend per werknemer → categorie → periode → hoofddocument → storting.</p>
      </div>

      <Card><CardContent className="p-5 space-y-5">
        <div>
          <Label>Categorieën</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            {HR_CATEGORY_ORDER.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-1.5">
                <Checkbox checked={cats.includes(c)} data-testid={`hr-exp-cat-${c}`} onCheckedChange={() => toggleCat(c)} /> {HR_CATEGORIES[c]}
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label>Van periode</Label><Input placeholder="JJJJ-MM" className="w-36" data-testid="hr-exp-start" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label>Tot periode</Label><Input placeholder="JJJJ-MM" className="w-36" data-testid="hr-exp-end" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-2">
            <Switch checked={newest} onCheckedChange={setNewest} data-testid="hr-exp-newest" /> Nieuwste eerst
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runPreview} disabled={loading} data-testid="hr-exp-preview-btn">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Laden…</> : <><Eye className="h-4 w-4 mr-2" /> Voorbeeld</>}
          </Button>
          <Button className="bg-teal-600 hover:bg-teal-700" disabled={gen || !preview || preview.count === 0} onClick={generate} data-testid="hr-exp-generate-btn">
            {gen ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genereren…</> : <><FileDown className="h-4 w-4 mr-2" /> PDF genereren</>}
          </Button>
        </div>
      </CardContent></Card>

      {preview && (
        <Card><CardContent className="p-5" data-testid="hr-exp-preview">
          <div className="text-sm text-muted-foreground mb-4">{preview.count} document(en) · {preview.employees.length} werknemer(s)</div>
          <div className="space-y-3">
            {preview.bundles.map((b) => {
              const st = HR_STATUS[b.status] || HR_STATUS.problem;
              return (
                <div key={b.id} className="flex items-center justify-between border rounded-md px-4 py-2.5">
                  <div className="text-sm">
                    <span className="font-medium">{empName(b.employee_id)}</span>
                    <span className="text-muted-foreground"> · {HR_CATEGORIES[b.category]} · {b.period_key || "—"} · {b.documents.length} doc.</span>
                  </div>
                  <Badge className={st.cls}>{st.label}</Badge>
                </div>
              );
            })}
            {preview.bundles.length === 0 && <div className="text-center text-muted-foreground py-6">Geen documenten voor deze selectie.</div>}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
